import {useState, useEffect, useRef} from 'react';

/**
 * @file Mesure la hauteur rendue d'un élément et la suit dans le temps.
 * @module packages.common.hooks.useElementHeight
 *
 * Sert à réserver dans le flux la place d'un élément sorti du flux
 * (`position: fixed`), sans figer sa hauteur en constante : un titre qui passe
 * sur deux lignes ou une langue plus verbeuse changent la hauteur réelle.
 */

/**
 * Suit la hauteur rendue d'un élément via ResizeObserver.
 *
 * @param {boolean} [enabled=true] - Désactive la mesure quand elle n'a pas lieu d'être
 * @return {Array} Paire `[ref, height]` : la ref à poser sur l'élément, puis sa hauteur en pixels
 *   (`null` tant qu'aucune mesure n'a eu lieu, pour distinguer « pas encore mesuré » de « vide »)
 */
export function useElementHeight(enabled = true) {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);

  useEffect(() => {
    const element = ref.current;

    if (!enabled || !element || typeof ResizeObserver === 'undefined') {
      setHeight(null);
      return undefined;
    }

    const measure = () => setHeight(element.getBoundingClientRect().height);

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [enabled]);

  return [ref, height];
}
